import { useCallback, useEffect, useRef } from 'react';

export const usePrevious = <Type>(value?: Type): Type | undefined => {
  const reference = useRef<Type>();
  useEffect(() => {
    reference.current = value;
  });
  return reference.current;
};

export const useChangeSelector = (values: { [key: string]: unknown }) => {
  const previous = useRef<{ [key: string]: unknown }>();

  const updatePreviousValues = useCallback(() => {
    previous.current = values;
  }, [values]);

  const hasChanged = useCallback(
    (keys: string | string[]): boolean => {
      if (typeof keys === 'string') {
        return previous.current?.[keys] !== values[keys];
      }
      return keys.findIndex((key) => previous.current?.[key] !== values[key]) >= 0;
    },
    [values]
  );

  return { updatePreviousValues, hasChanged, previousValues: previous.current };
};

export const useInterval = (callback: () => void, delay: number) => {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    // start immediatelly
    tick();
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

export const useSetTimeout = () => {
  const ongoingHandles = useRef<NodeJS.Timeout[]>([]);

  const setTimeoutManaged = useCallback((callback: () => void, delay: number) => {
    const handle = setTimeout(() => {
      ongoingHandles.current = ongoingHandles.current.filter((entry) => entry !== handle);
      callback();
    }, delay);
    ongoingHandles.current.push(handle);
  }, []);

  useEffect(() => {
    return () => {
      ongoingHandles.current.forEach((handle) => clearTimeout(handle));
    };
  }, []);

  return { setTimeoutManaged };
};
